import { MachineContext } from 'contexts/machine.context';
import useLatest from 'hooks/useLatestRef';
import { throttle } from 'lodash';
import { useContext, useEffect } from 'react';

interface IProps {
  // aka: fire/send/drop button
  onFire: () => void;
  onPrev: () => void;
  onNext: () => void;

  suspended?: boolean;
}

export const useRemoteListener = (props: IProps) => {
  const { machine } = useContext(MachineContext);
  const { remote_hotkeys: hotkeys } = machine;

  const latestPrev = useLatest(props.onPrev);
  const latestNext = useLatest(props.onNext);
  const latestFire = useLatest(props.onFire);

  useEffect(() => {
    if (props.suspended) {
      return;
    }

    const callback = throttle((event: KeyboardEvent) => {
      if (event.ctrlKey || event.shiftKey || event.altKey) {
        /** ignore everything with modifiers pressed */
        return;
      }

      if (
        event.code === hotkeys?.previous ||
        event.code === hotkeys?.next ||
        event.code === hotkeys?.fire
      ) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (hotkeys?.previous && event.code === hotkeys?.previous) {
        // console.debug('*** triggering prev');
        latestPrev.current();
        return;
      }

      if (hotkeys?.next && event.code === hotkeys?.next) {
        // console.debug('*** triggering next');
        latestNext.current();
        return;
      }

      if (hotkeys?.fire && event.code === hotkeys?.fire) {
        // console.debug('*** triggering fire');
        latestFire.current();
        return;
      }
    }, 500);

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [hotkeys, props.suspended]);
};
